<template>
  <b-row>
    <b-col cols="12" lg="8">
      <b-card>
        <b-tabs v-model="activeTab" nav-class="satellite-create-tabs">
          <b-tab>
            <template #title><b-icon icon="sliders" class="mr-1"></b-icon>{{ $t('satellites.form') }}</template>
            <b-form class="pt-3" @submit.prevent="save">
              <b-row>
                <b-col md="6">
                  <b-form-group :label="$t('satellites.id')">
                    <b-input v-model.trim="model.id" required placeholder="my-satellite" />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group :label="$t('satellites.name')">
                    <b-input v-model.trim="model.name" required />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-form-group :label="$t('satellites.description')">
                <b-input v-model.trim="model.description" />
              </b-form-group>

              <b-form-group :label="$t('satellites.endpoint')">
                <b-input v-model.trim="model.jsonrpc_endpoint_url" required placeholder="http://localhost:8787/jsonrpc" />
              </b-form-group>

              <b-row>
                <b-col md="6">
                  <b-form-group :label="$t('satellites.healthcheck')">
                    <b-input v-model.trim="model.health_check_url" placeholder="http://localhost:8787/health" />
                  </b-form-group>
                </b-col>
                <b-col md="6">
                  <b-form-group :label="$t('satellites.logs')">
                    <b-input v-model.trim="model.logs_url" placeholder="http://localhost:8787/logs" />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col md="4">
                  <b-form-group :label="$t('satellites.authType')">
                    <b-form-select v-model="model.auth_type" :options="authOptions" />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group :label="$t('satellites.headerName')">
                    <b-input v-model.trim="model.header_name" :disabled="model.auth_type !== 'api_key'" placeholder="X-Api-Key" />
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group :label="$t('secrets')">
                    <b-input v-model="model.secret" type="password" required autocomplete="new-password" />
                  </b-form-group>
                </b-col>
              </b-row>

              <div class="form-actions">
                <b-button variant="outline-secondary" @click="$router.push('/admin/developer/satellites')">
                  {{ $t('cancel') }}
                </b-button>
                <b-button type="submit" variant="success" :disabled="saving">
                  {{ $t('save') }}
                </b-button>
              </div>
            </b-form>
          </b-tab>

          <b-tab>
            <template #title><b-icon icon="braces" class="mr-1"></b-icon>{{ $t('satellites.json') }}</template>
            <div class="pt-3">
              <CodeEditor
                v-model="jsonPayload"
                lang="application/json"
                :withButton="false"
                :height="420"
                :options="{ mode: 'json', showFullscreenButton: true }"
              />

              <div class="form-actions mt-3">
                <b-button variant="outline-secondary" @click="$router.push('/admin/developer/satellites')">
                  {{ $t('cancel') }}
                </b-button>
                <b-button variant="success" @click="save" :disabled="saving">
                  {{ $t('save') }}
                </b-button>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import CodeEditor from '@/components/form/Editor/Code'
import { defaultSchema } from './helpers'

export default {
  name: 'DeveloperSatellitesCreate',
  components: {
    CodeEditor
  },
  data() {
    return {
      saving: false,
      activeTab: 0,
      authOptions: [
        { value: 'bearer', text: 'Bearer' },
        { value: 'api_key', text: 'API key' }
      ],
      model: {
        id: '',
        name: '',
        description: '',
        icon: 'broadcast-pin',
        jsonrpc_endpoint_url: '',
        health_check_url: '',
        logs_url: '',
        auth_type: 'bearer',
        header_name: '',
        secret: '',
        timeout_ms: 30000
      },
      jsonPayload: JSON.stringify({
        template: {
          id: 'my-satellite',
          label: { en_US: 'My Satellite' },
          description: { en_US: 'Project-owned JSON-RPC satellite.' },
          icon: 'broadcast-pin',
          jsonrpc_endpoint_url: 'https://satellite.example.com/jsonrpc',
          health_check_url: 'https://satellite.example.com/health',
          logs_url: 'https://satellite.example.com/logs',
          auth_type: 'bearer',
          auth_config: {
            secret: '<secret>'
          },
          timeout_ms: 30000
        },
        schema: defaultSchema()
      }, null, 2)
    }
  },
  methods: {
    payloadFromForm() {
      return {
        id: this.model.id,
        label: { en_US: this.model.name },
        description: { en_US: this.model.description },
        icon: this.model.icon,
        jsonrpc_endpoint_url: this.model.jsonrpc_endpoint_url,
        health_check_url: this.model.health_check_url,
        logs_url: this.model.logs_url,
        auth_type: this.model.auth_type,
        auth_config: {
          header_name: this.model.auth_type === 'api_key' ? this.model.header_name : '',
          secret: this.model.secret
        },
        timeout_ms: Number(this.model.timeout_ms) || 30000
      }
    },
    payloadFromJson() {
      try {
        return JSON.parse(this.jsonPayload)
      } catch (err) {
        this.$bvToast.toast(this.$t('satellites.invalidJson'), {
          title: this.$t('error'),
          variant: 'danger'
        })
        return null
      }
    },
    normalizeJsonPayload(payload) {
      if (payload.template) {
        return {
          template: payload.template,
          schema: payload.schema || (payload.schema_version ? this.schemaOnlyPayload(payload, ['template']) : null)
        }
      }

      if (payload.schema_version) {
        if (payload.jsonrpc_endpoint_url || payload.auth_type) {
          return {
            template: this.templateOnlyPayload(payload),
            schema: this.schemaOnlyPayload(payload)
          }
        }

        return {
          template: this.templateFromSchema(payload),
          schema: payload
        }
      }

      return {
        template: payload,
        schema: null
      }
    },
    templateOnlyPayload(payload) {
      const keys = [
        'id',
        'label',
        'description',
        'icon',
        'jsonrpc_endpoint_url',
        'health_check_url',
        'logs_url',
        'auth_type',
        'auth_config',
        'timeout_ms'
      ]
      return keys.reduce((template, key) => {
        if (payload[key] !== undefined) template[key] = payload[key]
        return template
      }, {})
    },
    schemaOnlyPayload(payload, omitKeys = []) {
      const schema = { ...payload }
      const templateKeys = [
        'id',
        'label',
        'description',
        'icon',
        'jsonrpc_endpoint_url',
        'health_check_url',
        'logs_url',
        'auth_type',
        'auth_config',
        'timeout_ms',
        ...omitKeys
      ]
      templateKeys.forEach((key) => delete schema[key])
      return schema
    },
    templateFromSchema(schema) {
      const template = this.payloadFromForm()
      const schemaName = schema.schema && schema.schema.name

      if (!template.id && schemaName) template.id = schemaName
      if (!template.label.en_US && schemaName) template.label = { en_US: schemaName }
      if (!template.description.en_US && schema.schema && schema.schema.description) {
        template.description = { en_US: schema.schema.description }
      }

      return template
    },
    validateTemplatePayload(template) {
      if (!template.id || !template.jsonrpc_endpoint_url || !template.auth_type) return false
      if (!['bearer', 'api_key'].includes(template.auth_type)) return false
      if (!template.auth_config || !template.auth_config.secret) return false
      if (template.auth_type === 'api_key' && !template.auth_config.header_name) return false
      return true
    },
    async save() {
      const jsonPayload = this.activeTab === 1 ? this.payloadFromJson() : null
      if (this.activeTab === 1 && !jsonPayload) return

      const normalized = this.activeTab === 1
        ? this.normalizeJsonPayload(jsonPayload)
        : { template: this.payloadFromForm(), schema: null }

      if (!normalized || !normalized.template) return

      if (!this.validateTemplatePayload(normalized.template)) {
        this.$bvToast.toast(this.$t('satellites.invalidTemplateJson'), {
          title: this.$t('error'),
          variant: 'danger'
        })
        return
      }

      this.saving = true

      try {
        const resp = await this.$integrationApi.post('/satellites/templates', normalized.template)
        if (normalized.schema) {
          await this.$integrationApi.post(`/satellites/templates/${resp.data.id}/versions`, normalized.schema)
        }

          this.$bvToast.toast(this.$t('satellites.created'), {
            title: this.$t('success'),
            variant: 'success'
          })
          this.$router.push(`/admin/developer/satellites/${resp.data.id}`)
      } catch (err) {
        this.$debugError(err)
        const message = err.response && err.response.data && err.response.data.message
        this.$bvToast.toast(message || this.$t('satellites.failedToCreate'), {
          title: this.$t('error'),
          variant: 'danger'
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
